// src/context/NetworkProvider.tsx
import React, { createContext, useContext, useState } from 'react';
import { SYNC_STATUS, SyncTx } from './types/NetworkTypes';
import { SolanaContext} from './SolanaContext';
import { BscContext } from './BscContext';
import { TonContext } from './TonContext';

// 네트워크 분기 시 사용할 인터페이스(함수 시그니처들)
// 여기서는 간단히 syncStatus, startDbSyncTimer만 예시로...
interface NetworkProps {
  networkName: string,
	balance: number,
	countSyncProcess: number,
	countSyncComplete: number,
	syncStatus: string,
	lastSyncTxs: SyncTx[],
	reqTxStop: boolean,
  startDbSyncTimer: () => void,
	stopDbSyncTimer: () => void,
	getBalance: (walletAddress: string, isFormatting?: boolean, updateType?: string) => Promise<number>,
	getBalanceFromServer: (walletAddress: string, coinName: string) => Promise<number>,
	getTokenBalance: (walletAddress: string, mintAddress: string, isFormatting?: boolean, updateType?: string) => Promise<string>,
	getTokenBalanceFromServer: (walletAddress: string, mintAddress: string, coinName: string) => Promise<number>,
	estimateGas: (walletAddress: string) => Promise<string>,
	sendTransactionWithPrivateKey: (privateKey: string, toAddress: string, amount: string, tokenSymbol: string, tokenAddress: string, publicKey: string) => Promise<string>,
	getTotalTxCount: (walletAddress: string) => Promise<any>,
	stopRequestTxData: (isStop?:boolean) => void,
	syncAllTransaction: (walletAddress: string) => Promise<any>,
	getTokenInfo: (mintAddress: string) => Promise<any>,
	setSelectedNetwork: (networkName: string) => void,
	getServerTxHistory: (walletAddress: string, network: string, tid: string, tokenName: string, page: number, take: number) => Promise<any>
  getBlockScanUrl: (txid: string) => Promise<string>,
  isValidAddress: (address: string) => Promise<Boolean>
}

export const NetworkContext = createContext<NetworkProps>({
  networkName: '',
	balance: 0,
	countSyncProcess: 0,
	countSyncComplete: 0,
	syncStatus: SYNC_STATUS.CHECKING,
	lastSyncTxs: [],
	reqTxStop: false,
	startDbSyncTimer: async () => {},
	stopDbSyncTimer: async () => {},
	getBalance: async (walletAddress: string, isFormatting = true, updateType = 'await') => Promise.resolve(0),
	getBalanceFromServer: async (walletAddress: string, coinName: string) => Promise.resolve(0),
	getTokenBalance: async (walletAddress: string, mintAddress: string, isFormatting = true, updateType = 'await') => Promise.resolve('0.0'),
	getTokenBalanceFromServer: async (walletAddress: string, mintAddress: string, coinName: string) => Promise.resolve(0),
	estimateGas: async (walletAddress: string) => Promise.resolve('0'),
	sendTransactionWithPrivateKey: async (privateKey: string, toAddress: string, amount: string, tokenSymbol: string, tokenAddress: string, publicKey: string) => Promise.resolve(''),
	getTotalTxCount: async (walletAddress: string) => Promise.resolve(0),
	stopRequestTxData: (isStop?:boolean) => {},
	syncAllTransaction: async (walletAddress: string) => Promise.resolve([]),
	getTokenInfo: async (mintAddress: string) => Promise.resolve(null),
	setSelectedNetwork: (networkName: string) => {},
	getServerTxHistory: async (walletAddress: string, network: string, tid: string, tokenName: string, page: number, take: number) => Promise.resolve([]),
  getBlockScanUrl: (txid: string) => Promise.resolve(''),
  isValidAddress: async (address: string) => Promise.resolve(false)
});


export function NetworkProvider({ children }: { children: React.ReactNode }) {
  // 네트워크 선택 상태
  // (로컬 스토리지나 secureStorage에서 가져올 수도 있고, 
  //  드롭다운으로 setSelectedNetwork를 바꿀 수도 있음)
  const [selectedNetwork, setSelectedNetwork] = useState(() => {
    const savedNetwork = localStorage.getItem('blockChain');
    return savedNetwork;
  });

  
  // 두 Context에서 제공하는 값
  const solana = useContext(SolanaContext); 
  const bsc = useContext(BscContext);
  const ton = useContext(TonContext);

  // 선택된 네트워크에 따라 반환할 값 결정
  let selectedContext;

  switch(selectedNetwork) {
    case 'solana':
      selectedContext = solana;
      console.log(`선택된 NetworkProvider : solana`, selectedNetwork)
      break;
    case 'bsc':
      selectedContext = bsc;
      console.log(`선택된 NetworkProvider : bsc`, selectedContext)
      break;
    case 'ton':
        selectedContext = ton;
        console.log(`선택된 NetworkProvider : ton`, selectedContext)
        break;
    // 추후 다른 네트워크 추가 시 case 추가
    // case 'eth':
    //   selectedContext = eth;
    //   break;
    default:
      selectedContext = solana; // 기본값
  }

  const value: NetworkProps = {
    networkName: selectedContext.networkName,
    balance: selectedContext.balance, 
    countSyncProcess: selectedContext.countSyncProcess,
    countSyncComplete: selectedContext.countSyncComplete,
    syncStatus: selectedContext.syncStatus,
    lastSyncTxs: selectedContext.lastSyncTxs,
    reqTxStop: selectedContext.reqTxStop,
    startDbSyncTimer: selectedContext.startDbSyncTimer,
    stopDbSyncTimer: selectedContext.stopDbSyncTimer,
    getBalance: selectedContext.getBalance,
    getBalanceFromServer: selectedContext.getBalanceFromServer, 
    getTokenBalance: selectedContext.getTokenBalance,
    getTokenBalanceFromServer: selectedContext.getTokenBalanceFromServer,
    estimateGas: selectedContext.estimateGas,
    sendTransactionWithPrivateKey: selectedContext.sendTransactionWithPrivateKey,
    getTotalTxCount: selectedContext.getTotalTxCount,
    stopRequestTxData: selectedContext.stopRequestTxData,
    syncAllTransaction: selectedContext.syncAllTransaction,
    getTokenInfo: selectedContext.getTokenInfo,
    setSelectedNetwork: setSelectedNetwork,
		getServerTxHistory: selectedContext.getServerTxHistory,
    getBlockScanUrl: selectedContext.getBlockScanUrl,
    isValidAddress: selectedContext.isValidAddress,
  };


  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
}

// 네트워크 분기 전환 함수를 쓰려면, 
//  - setSelectedNetwork를 외부로 노출하거나
//  - setSelectedNetwork를 외부로 노출하려면 value 객체에 setSelectedNetwork 추가
//  - value = { ...networkProps, setSelectedNetwork }
//  - useContext(NetworkContext) 대신 다른 구조를 더 구현할 수도 있음
